import { useId, useState } from "react";

import {
  Button,
  Checkbox,
  InfoModal,
  RadioButton,
  ToggleButton,
} from "@newsela/angelou";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  useTracker,
  eventTypes,
  actionPrefixes,
} from "@newsela/snitch";
import { getPrintAssignmentLink } from "@/assignments/utils/assignmentUtils";
import { EventProperties } from "@/types/models/EventProperties.interface";
import EcoModeToolTipDialog from "@/view/PrintSelector/PrintModal/EcoModeToolTipDialog";

import { getContentQueryParamFromSelectedOptions } from "../getPrintModalOptions";
import { PrintOption } from "../types/PrintOption.interface";
import getPrintButtonEventProperties from "../utils/getPrintButtonEventProperties";
import * as styles from "./PrintModal.style";

interface IModalProps {
  isActive: boolean;
  closeModal: () => void;
}

interface IPrintModal {
  assignmentId?: string | undefined;
  baseUrl: string;
  currentLevelId: string;
  eventProperties: EventProperties;
  printOptions: PrintOption[];
  PrintModalProps: IModalProps;
}

export interface IPrintRadioOption {
  label: string;
  value: string;
}

const PrintModal = ({
  assignmentId,
  currentLevelId,
  baseUrl,
  eventProperties,
  printOptions,
  PrintModalProps,
}: IPrintModal) => {
  const CheckboxWithTrackEvent = withTrackEvent(Checkbox) as typeof Checkbox;
  const ToggleButtonWithTrackEvent = withTrackEvent(ToggleButton);
  const RadioButtonWithTrackEvent = withTrackEvent(RadioButton);
  const printAssignmentOrArticleRadioButtons: IPrintRadioOption[] = [
    {
      label: "Assignment",
      value: "assignment",
    },
    {
      label: "Article",
      value: "article_with_activities",
    },
  ];
  const [selectedOption, setSelectedOption] = useState<
    IPrintRadioOption | undefined
  >(printAssignmentOrArticleRadioButtons[0]);

  const [selectedPrintOptions, setSelectedPrintOptions] = useState<string[]>(
    [],
  );

  const handleOptionChange = (option: IPrintRadioOption) => {
    setSelectedOption(option);
    // Clear all print options when re-opening modal
    setSelectedPrintOptions([]);
  };

  const labelId = `PrintModal${useId()}`;
  const { trackAction, getEventName } = useTracker();

  const handleCloseModalTracking = () => {
    trackAction({
      event_name: getEventName(
        objectTypes.user,
        actionPrefixes.cancel,
        actionNames.print,
      ),
      event_type: eventTypes.click,
      dimension_properties: {
        description: "Close print options modal.",
      },
    });
  };

  const handleCloseModal = () => {
    PrintModalProps.closeModal();
    // Clear all print options when re-opening modal
    setSelectedPrintOptions([]);
    // TODO: Do we need to track closing the modal in this function?
    // handleCloseModalTracking();
  };

  const handlePrintOptionClick = (option: PrintOption) => {
    if (selectedPrintOptions.includes(option.queryParam)) {
      setSelectedPrintOptions(
        selectedPrintOptions.filter((selectedOption) => {
          return option.queryParam !== selectedOption;
        }),
      );
    } else {
      setSelectedPrintOptions([...selectedPrintOptions, option.queryParam]);
    }
  };

  const optionCheckbox = (option: PrintOption) => {
    return (
      <CheckboxWithTrackEvent
        /* @ts-expect-error Property 'children' does not exist on type */
        actionName={actionNames.print}
        checked={selectedPrintOptions.includes(option.queryParam)}
        dataProps={{
          "data-qa-selector": `print-modal-${option?.label}`,
        }}
        label={option?.label}
        name="Print Option"
        onClick={() => handlePrintOptionClick(option)}
        value={option.queryParam}
        withContainer
        {...getPrintButtonEventProperties(option, eventProperties, true)}
      />
    );
  };

  const ecoModeToggle = (option: PrintOption) => {
    return (
      <div css={styles.ecoModeToggleCss}>
        {/* @ts-expect-error dataProps was throwing error */}
        <ToggleButtonWithTrackEvent
          dataProps={{
            "data-qa-selector": `print-modal-${option?.label}`,
          }}
          label={option?.label}
          pressed={selectedPrintOptions.includes(option.queryParam)}
          onClick={() => handlePrintOptionClick(option)}
          {...getPrintButtonEventProperties(option, eventProperties, true)}
        />
        <EcoModeToolTipDialog currentContentLevelId={currentLevelId} />
      </div>
    );
  };

  const printAssignmentLink = assignmentId
    ? getPrintAssignmentLink(assignmentId, currentLevelId)
    : null;
  const ecoPrintOption = printOptions.find(
    (option) => option?.queryParam === "eco_mode",
  );

  const printArticleOptions = (
    <>
      <ul css={styles.ulCss}>
        {printOptions?.map((option) => {
          if (!option) return null;
          return (
            <li
              key={option?.queryParam}
              css={styles.checkBoxAndTooltipContainerCss}
            >
              {option.queryParam !== "eco_mode" && optionCheckbox(option)}
            </li>
          );
        })}
      </ul>
      {ecoPrintOption && ecoModeToggle(ecoPrintOption)}
    </>
  );

  let assignmentOrArticleRadioButtons = <></>;
  if (printAssignmentLink) {
    // For assignments, the only additional print option is Eco-Friendly Mode
    const printAssignmentOptions = ecoPrintOption ? (
      ecoModeToggle(ecoPrintOption)
    ) : (
      <></>
    );

    assignmentOrArticleRadioButtons = (
      <div css={styles.wrapperCss}>
        <fieldset css={styles.fieldsetCss}>
          <div css={styles.radioOptionsWrapperCss} role="radiogroup">
            {printAssignmentOrArticleRadioButtons.map((radioOption) => {
              return (
                <RadioButtonWithTrackEvent
                  actionName={actionNames.print}
                  checked={selectedOption?.value === radioOption.value}
                  dataProps={{
                    "data-qa-selector": `print-select-${radioOption.value}`,
                  }}
                  key={radioOption.value}
                  label={radioOption.label}
                  name="printAssignmentOrArticleRadioButtons"
                  objectType={objectTypes.content}
                  onChange={() => handleOptionChange(radioOption)}
                  streamProperties={{
                    content_id: currentLevelId,
                    content_type: radioOption.label,
                    description: `Choose to print ${radioOption.label} in modal`,
                  }}
                  /* @ts-expect-error radioOption */
                  value={radioOption}
                  __cssFor={{
                    root: styles.optionCss,
                  }}
                />
              );
            })}
          </div>
        </fieldset>
        {selectedOption?.value === "article_with_activities"
          ? printArticleOptions
          : printAssignmentOptions}
      </div>
    );
  }

  const handlePrint = () => {
    let hrefValue: string | null = "";
    if (selectedOption?.value === "assignment" && printAssignmentLink) {
      hrefValue = printAssignmentLink;
    } else {
      // For original print options, combine into the base query param for "&content="
      const basePrintQueryParam =
        getContentQueryParamFromSelectedOptions(selectedPrintOptions);
      hrefValue = `${baseUrl}${basePrintQueryParam}`;
    }
    // Newer print options are all treated as individual query params
    const ecoModeQueryParam = selectedPrintOptions.includes("eco_mode")
      ? "&eco_mode"
      : "";
    const powerWordsQueryParam = selectedPrintOptions.includes("power_words")
      ? "&power_words"
      : "";
    const powerWordsAnswerQueryParam = selectedPrintOptions.includes(
      "power_words_answers",
    )
      ? "&power_words_answers"
      : "";

    hrefValue = `${hrefValue}${ecoModeQueryParam}${powerWordsQueryParam}${powerWordsAnswerQueryParam}`;
    window.open(hrefValue);
  };

  return (
    <InfoModal
      __cssFor={styles.shareModalCss}
      labelId={labelId}
      onBackgroundClick={handleCloseModal}
      onClose={handleCloseModalTracking}
      onEsc={handleCloseModal}
      footer={
        <div css={styles.footerButtonsWrapper}>
          <Button
            flavor={Button.flavors.tertiary}
            onClick={() => handleCloseModal()}
            dataProps={{ "data-qa-selector": "print-modal-cancel" }}
          >
            Cancel
          </Button>
          <Button
            flavor={Button.flavors.primary}
            onClick={() => handlePrint()}
            dataProps={{ "data-qa-selector": "print-modal-print" }}
          >
            Print
          </Button>
        </div>
      }
      title="Print Options"
      {...PrintModalProps}
    >
      {printAssignmentLink
        ? assignmentOrArticleRadioButtons
        : printArticleOptions}
    </InfoModal>
  );
};

export default PrintModal;
